import React from 'react';
import { ImageOff } from 'lucide-react';

/**
 * <ImageWithFallback /> — drop-in replacement for <img> that gracefully
 * handles broken/missing image URLs by rendering a fallback element instead
 * of the browser's default broken-image icon.
 *
 * Props:
 *   src                - image URL (falsy → fallback rendered immediately)
 *   alt                - alt text (default '')
 *   className          - applied to the <img> when src loads
 *   fallback           - custom React node rendered on error / when src is empty.
 *                        Pass this to match the parent UI tone.
 *   fallbackClassName  - convenience: applied to default fallback wrapper when
 *                        no custom `fallback` is provided. Defaults to className.
 *   imgProps           - any extra props forwarded to the underlying <img>
 *
 * Usage:
 *   <ImageWithFallback
 *     src={svc.image_url}
 *     className="w-full h-full object-cover"
 *     fallback={<ShoppingBag className="w-6 h-6 text-[#5C7599]" />}
 *   />
 */
export function ImageWithFallback({
  src,
  alt = '',
  className = '',
  fallback,
  fallbackClassName,
  ...imgProps
}) {
  const [broken, setBroken] = React.useState(false);
  const [lastSrc, setLastSrc] = React.useState(src);

  // Reset broken flag when the source URL changes (e.g. after a re-upload).
  // Using the "adjust state during render" pattern instead of useEffect.
  if (src !== lastSrc) {
    setLastSrc(src);
    setBroken(false);
  }

  if (!src || broken) {
    if (fallback) {
      // Caller-supplied fallback (already styled to match the surrounding UI).
      return <>{fallback}</>;
    }
    return (
      <div
        className={`flex items-center justify-center bg-[#000B18]/60 text-[#5C7599] ${fallbackClassName || className}`}
        role="img"
        aria-label={alt ? `${alt} unavailable` : 'Image unavailable'}
        data-testid="image-fallback"
      >
        <ImageOff className="w-6 h-6 opacity-60" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setBroken(true)}
      {...imgProps}
    />
  );
}

export default ImageWithFallback;
